import React, { useState } from 'react';
import { Loader2, AlertTriangle, FileText } from 'lucide-react';
import { api, type Profile } from '../lib/api';
import { AppIcon } from './Logo';
import { LegalLink } from './ProfileFields';
import { AccountDataPanel } from './AccountDataPanel';

interface Props {
  profile: Profile;
  onAccepted: (profile: Profile) => void;
  /** Cerró sesión o borró la cuenta. */
  onSignedOut: (message?: string) => void;
}

/**
 * Lo que una cuenta que ya existía tiene que resolver antes de seguir.
 *
 * Dos casos:
 *
 *  - No ha aceptado la versión vigente de los términos: las cuentas anteriores
 *    a que existieran, o cualquiera después de un cambio de fondo.
 *  - Su edad ya no alcanza para su país con las reglas actuales. No se le
 *    borra la cuenta sin avisar: se le explica y se le deja descargar sus
 *    datos y borrarla, o cerrar sesión.
 *
 * Como el de completar el perfil, no se puede cerrar: dejar seguir sin
 * resolverlo haría el requisito decorativo.
 */
export const TermsGateModal: React.FC<Props> = ({ profile, onAccepted, onSignedOut }) => {
  const [acepta, setAcepta] = useState(false);
  const [gestionar, setGestionar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const aceptar = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await api.post<{ profile: Profile }>('/auth/accept-terms', { acceptTerms: true });
      onAccepted(data.profile);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gate-title"
    >
      <div className="surface-raised bg-ink-900 w-full max-w-[28rem] my-auto animate-pop">
        <header className="flex items-start gap-3 px-6 pt-6 pb-2">
          <AppIcon size={32} />
          <div className="flex-1 min-w-0">
            <h2 id="gate-title" className="font-display font-semibold text-[1.0625rem] text-ink-50">
              {profile.ageAllowed ? 'Hemos actualizado nuestros términos' : 'Tu cuenta no puede seguir activa'}
            </h2>
          </div>
        </header>

        {profile.ageAllowed ? (
          <>
          <form onSubmit={aceptar} className="px-6 pb-6 pt-2 space-y-4">
            <p className="text-ink-300 text-[0.8125rem] leading-relaxed">
              Para seguir usando tu cuenta necesitamos que leas y aceptes los Términos de uso y la
              Política de privacidad. Explican qué datos guardamos, para qué, qué puedes hacer con
              ellos y las reglas para usar Open Dahu.
            </p>

            <div className="flex gap-2 text-[0.8125rem]">
              <FileText className="w-4 h-4 text-ink-500 shrink-0 mt-px" />
              <span className="text-ink-400">
                <LegalLink to="terminos">Términos de uso</LegalLink> ·{' '}
                <LegalLink to="privacidad">Política de privacidad</LegalLink>
              </span>
            </div>

            <label className="flex items-start gap-2.5 text-[0.8125rem] text-ink-300 cursor-pointer">
              <input
                type="checkbox"
                checked={acepta}
                onChange={(e) => setAcepta(e.target.checked)}
                className="mt-0.5 w-4 h-4 shrink-0 accent-ember-500 cursor-pointer"
              />
              He leído y acepto los Términos de uso y la Política de privacidad.
            </label>

            {error && (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-danger-400/30 bg-danger-400/10 px-3 py-2.5 text-danger-400 text-[0.8125rem]"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={!acepta || busy}
              className="w-full flex items-center justify-center gap-2 h-11 rounded-lg font-semibold
                         bg-ember-500 hover:bg-ember-400 text-on-accent
                         disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed transition-colors"
            >
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              Aceptar y continuar
            </button>
            <button
              type="button"
              onClick={() => onSignedOut()}
              className="w-full text-center text-[0.8125rem] text-ink-400 hover:text-ink-100"
            >
              No acepto, cerrar sesión
            </button>
            <p className="text-[0.6875rem] leading-relaxed text-ink-500">
              Si no los aceptas puedes seguir jugando como invitado.{' '}
              <button
                type="button"
                onClick={() => setGestionar((v) => !v)}
                className="text-sky-400 hover:text-sky-300 underline underline-offset-2"
              >
                Descargar mis datos o borrar mi cuenta
              </button>
            </p>
          </form>
          {gestionar && (
            <div className="border-t border-ink-800">
              <AccountDataPanel
                profile={profile}
                onDeleted={() => onSignedOut('Tu cuenta y sus datos se han borrado.')}
              />
            </div>
          )}
          </>
        ) : (
          <>
            <p className="px-6 pt-2 text-ink-300 text-[0.8125rem] leading-relaxed">
              Con las reglas de edad que aplican en tu país ({profile.countryName ?? 'el que indicaste'}),
              todavía no tienes la edad mínima para tener una cuenta. Puedes seguir jugando como
              invitado con el PIN de una partida, sin cuenta. Si lo prefieres, descarga tus datos y
              borra la cuenta.
            </p>
            <AccountDataPanel
              profile={profile}
              onDeleted={() => onSignedOut('Tu cuenta y sus datos se han borrado.')}
            />
            <div className="px-6 pb-6">
              <button
                type="button"
                onClick={() => onSignedOut()}
                className="w-full h-11 rounded-lg text-sm font-semibold bg-ink-850 hover:bg-ink-800 border border-ink-700 text-ink-100"
              >
                Cerrar sesión
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
